import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { ManualeButton } from "@/components/ManualeButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ListChecks, Plus, Pencil, Trash2, ArrowLeft, CalendarDays, CalendarRange, CalendarClock, Sun, User } from "lucide-react";

const GIORNI = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const CAT_COLOR = {
  incassi: "bg-emerald-100 text-emerald-700", bonifici: "bg-blue-100 text-blue-700",
  quietanzamento: "bg-amber-100 text-amber-700", giornaliere: "bg-violet-100 text-violet-700",
  stipendi: "bg-teal-100 text-teal-700", subagenzia: "bg-orange-100 text-orange-700",
  assimoco: "bg-indigo-100 text-indigo-700", estratti: "bg-cyan-100 text-cyan-700",
  formazione: "bg-pink-100 text-pink-700", altro: "bg-slate-100 text-slate-600",
};

export default function ChiFaCosa() {
  const navigate = useNavigate();
  const [agenda, setAgenda] = useState(null);
  const loadAgenda = () => api.get("/mansioni/agenda").then((r) => setAgenda(r.data)).catch(() => setAgenda(null));
  useEffect(() => { loadAgenda(); }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <button data-testid="mans-back-btn" onClick={() => navigate("/gestione-agenzia")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft className="h-4 w-4" /> Gestione Agenzia</button>
        <div className="flex items-start justify-between gap-3 mb-8 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center"><ListChecks className="h-6 w-6" /></div>
            <div>
              <h1 className="font-heading text-3xl font-medium text-slate-900">Chi fa cosa</h1>
              <p className="text-slate-500 text-sm">Responsabilità e checklist ricorrenti dell'agenzia.{agenda ? ` Oggi è ${agenda.giorno}.` : ""}</p>
            </div>
          </div>
          <ManualeButton route="/manuale/chi-fa-cosa" />
        </div>

        <Tabs defaultValue="agenda">
          <TabsList data-testid="mans-tabs">
            <TabsTrigger value="agenda" data-testid="tab-agenda"><Sun className="h-4 w-4 mr-1.5" />Da fare</TabsTrigger>
            <TabsTrigger value="mansioni" data-testid="tab-mansioni"><ListChecks className="h-4 w-4 mr-1.5" />Attività & responsabili</TabsTrigger>
          </TabsList>
          <TabsContent value="agenda" className="mt-6"><AgendaTab agenda={agenda} reload={loadAgenda} /></TabsContent>
          <TabsContent value="mansioni" className="mt-6"><MansioniTab reloadAgenda={loadAgenda} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function CatBadge({ cat }) {
  return <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${CAT_COLOR[cat] || CAT_COLOR.altro}`}>{cat}</span>;
}

function AgendaGroup({ title, icon: Icon, items, onToggle, testid }) {
  if (!items || items.length === 0) return null;
  const daFare = items.filter((i) => !i.completato).length;
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-cyan-600" />
        <h3 className="font-heading text-lg text-slate-800">{title}</h3>
        {daFare > 0 && <Badge variant="secondary" className="bg-orange-100 text-orange-700">{daFare} da fare</Badge>}
      </div>
      <div className="space-y-2">
        {items.map((m) => (
          <label key={m.id} data-testid={`agenda-item-${m.id}`}
            className={`flex items-start gap-3 rounded-lg border p-3 bg-white cursor-pointer transition-all ${m.completato ? "border-slate-200 opacity-60" : "border-slate-200 hover:border-cyan-300"}`}>
            <Checkbox checked={m.completato} onCheckedChange={() => onToggle(m)} data-testid={`agenda-toggle-${m.id}`} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-medium ${m.completato ? "line-through text-slate-400" : "text-slate-800"}`}>{m.titolo}</span>
                <CatBadge cat={m.categoria} />
                {m.giorno_settimana != null && <span className="text-[11px] text-slate-400">{GIORNI[m.giorno_settimana]}</span>}
              </div>
              {m.descrizione && <div className="text-xs text-slate-500 mt-0.5">{m.descrizione}</div>}
            </div>
            {m.responsabile && <span className="inline-flex items-center gap-1 text-xs text-slate-500 shrink-0"><User className="h-3.5 w-3.5" />{m.responsabile}</span>}
          </label>
        ))}
      </div>
    </section>
  );
}

function AgendaTab({ agenda, reload }) {
  const toggle = async (m) => {
    try { await api.post(`/mansioni/${m.id}/toggle`, { periodo: m.periodo }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  if (!agenda) return <div className="text-slate-400 text-sm">Caricamento…</div>;
  const empty = agenda.oggi.length + agenda.settimana.length + agenda.mese.length + agenda.una_tantum.length === 0;
  if (empty) return <div data-testid="agenda-empty" className="text-center py-16 rounded-2xl border border-dashed border-slate-300 bg-white text-slate-500">Nessuna attività definita. Aggiungile in "Attività & responsabili".</div>;
  return (
    <div>
      <AgendaGroup title="Oggi" icon={CalendarDays} items={agenda.oggi} onToggle={toggle} testid="agenda-oggi" />
      <AgendaGroup title="Questa settimana" icon={CalendarRange} items={agenda.settimana} onToggle={toggle} testid="agenda-settimana" />
      <AgendaGroup title="Questo mese" icon={CalendarClock} items={agenda.mese} onToggle={toggle} testid="agenda-mese" />
      <AgendaGroup title="Una tantum" icon={CalendarClock} items={agenda.una_tantum} onToggle={toggle} testid="agenda-unatantum" />
    </div>
  );
}

function MansioniTab({ reloadAgenda }) {
  const [rows, setRows] = useState([]);
  const [opts, setOpts] = useState({ categorie: [], ricorrenze: [] });
  const [resp, setResp] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/mansioni").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    load();
    api.get("/mansioni/opzioni").then((r) => setOpts(r.data)).catch(() => {});
    api.get("/mansioni/responsabili").then((r) => setResp(r.data)).catch(() => {});
  }, []);
  const empty = { titolo: "", descrizione: "", responsabile: "", categoria: "altro", ricorrenza: "giornaliera", giorno_settimana: null, scadenza: "", attiva: true };
  const save = async () => {
    if (!dlg.titolo?.trim()) return toast.error("Titolo obbligatorio");
    try {
      const payload = { ...dlg, giorno_settimana: dlg.giorno_settimana === "" ? null : dlg.giorno_settimana };
      if (dlg.id) await api.put(`/mansioni/${dlg.id}`, payload);
      else await api.post("/mansioni", payload);
      toast.success("Salvato"); setDlg(null); load(); reloadAgenda();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => { if (!window.confirm(`Eliminare "${r.titolo}"?`)) return; await api.delete(`/mansioni/${r.id}`); load(); reloadAgenda(); };

  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="mans-add" onClick={() => setDlg({ ...empty })}><Plus className="h-4 w-4 mr-1.5" />Nuova attività</Button></div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Attività</TableHead><TableHead>Categoria</TableHead><TableHead>Ricorrenza</TableHead><TableHead>Responsabile</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`mans-row-${r.id}`}>
                <TableCell className="font-medium">{r.titolo}</TableCell>
                <TableCell><CatBadge cat={r.categoria} /></TableCell>
                <TableCell className="capitalize">{r.ricorrenza}{r.giorno_settimana != null ? ` · ${GIORNI[r.giorno_settimana]}` : ""}</TableCell>
                <TableCell>{r.responsabile || "—"}</TableCell>
                <TableCell>{r.attiva ? <span className="text-green-600 text-xs">Attiva</span> : <span className="text-slate-400 text-xs">Sospesa</span>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => setDlg({ ...r, giorno_settimana: r.giorno_settimana })} className="text-slate-400 hover:text-brand-blue mr-3" data-testid={`mans-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-500" data-testid={`mans-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nessuna attività definita.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="mans-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuova"} attività</DialogTitle><DialogDescription>Assegna responsabile e ricorrenza per la checklist.</DialogDescription></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label>Titolo</Label><Input value={dlg.titolo} onChange={(e) => setDlg({ ...dlg, titolo: e.target.value })} data-testid="mans-titolo" /></div>
              <div><Label>Descrizione</Label><Textarea value={dlg.descrizione} onChange={(e) => setDlg({ ...dlg, descrizione: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Categoria</Label><Select value={dlg.categoria} onValueChange={(v) => setDlg({ ...dlg, categoria: v })}><SelectTrigger data-testid="mans-categoria"><SelectValue /></SelectTrigger><SelectContent>{opts.categorie.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Ricorrenza</Label><Select value={dlg.ricorrenza} onValueChange={(v) => setDlg({ ...dlg, ricorrenza: v })}><SelectTrigger data-testid="mans-ricorrenza"><SelectValue /></SelectTrigger><SelectContent>{opts.ricorrenze.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace("_", " ")}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Responsabile</Label>
                  <Input list="resp-list" value={dlg.responsabile} onChange={(e) => setDlg({ ...dlg, responsabile: e.target.value })} data-testid="mans-responsabile" placeholder="Nome" />
                  <datalist id="resp-list">{resp.map((r) => <option key={r} value={r} />)}</datalist>
                </div>
                {dlg.ricorrenza === "settimanale" && (
                  <div><Label>Giorno</Label><Select value={dlg.giorno_settimana == null ? "any" : String(dlg.giorno_settimana)} onValueChange={(v) => setDlg({ ...dlg, giorno_settimana: v === "any" ? null : Number(v) })}><SelectTrigger data-testid="mans-giorno"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="any">Qualsiasi</SelectItem>{GIORNI.map((g, i) => <SelectItem key={i} value={String(i)}>{g}</SelectItem>)}</SelectContent></Select></div>
                )}
                {dlg.ricorrenza === "una_tantum" && (
                  <div><Label>Scadenza</Label><Input type="date" value={dlg.scadenza} onChange={(e) => setDlg({ ...dlg, scadenza: e.target.value })} /></div>
                )}
              </div>
              <div className="flex items-center gap-2"><Checkbox checked={dlg.attiva} onCheckedChange={(v) => setDlg({ ...dlg, attiva: !!v })} /><Label>Attiva</Label></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="mans-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
